import PolicyLayout, { PolicySection } from "@/components/PolicyLayout";

const LAST_UPDATED = "May 15, 2026";

export default function RefundPolicyPage() {
  return (
    <PolicyLayout
      title="Refund & Return Policy"
      subtitle="Returns"
      lastUpdated={LAST_UPDATED}
      testId="refund-policy-page"
    >
      <p>
        We stand behind every part we sell. If something isn't right, we want to make it right. This policy explains
        when a full refund applies and when restocking fees apply.
      </p>

      <PolicySection title="Full Refunds (No Restocking Fee)">
        <p>You are entitled to a <strong className="text-white">full refund</strong> if the item is:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong className="text-white">Defective</strong> — manufacturing defect or material flaw not caused by use or improper installation.</li>
          <li><strong className="text-white">Improperly described</strong> — the product you received does not match the description, photographs, or specifications listed on our website.</li>
          <li><strong className="text-white">Out of spec</strong> — dimensions, finish, or material composition fall outside the published tolerances for the part.</li>
        </ul>
        <p>
          For full-refund claims, contact us within <strong className="text-white">14 days</strong> of delivery with
          photos and a brief description. Once we confirm the issue, we will provide a prepaid return label and issue
          a full refund (including original shipping) within 5 business days of receiving the returned item.
        </p>
      </PolicySection>

      <PolicySection title="Other Returns (20% Restocking Fee Applies)">
        <p>
          For any return that is <em>not</em> due to a defect, misdescription, or out-of-spec issue (e.g., buyer's
          remorse, incorrect part ordered for your firearm, changed mind, or any other reason), the following terms
          apply:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>A <strong className="text-white">20% restocking fee</strong> will be deducted from the refund amount.</li>
          <li>Original <strong className="text-white">shipping and handling charges are non-refundable</strong>.</li>
          <li>The customer is responsible for <strong className="text-white">return shipping costs</strong>.</li>
          <li>Item must be returned in its <strong className="text-white">original, unused condition</strong> with all original packaging.</li>
        </ul>
        <p>Returns of this type must be initiated within <strong className="text-white">30 days</strong> of delivery.</p>
      </PolicySection>

      <PolicySection title="Non-Returnable Items">
        <p>The following items cannot be returned under any circumstances:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Items that have been installed, modified, refinished, fitted, or test-fired.</li>
          <li>Items damaged by misuse, improper installation, or attempted modification by the customer or a third party.</li>
          <li>Custom or made-to-order parts produced specifically for your order.</li>
          <li>Items marked "Final Sale" or "Closeout" at the time of purchase.</li>
        </ul>
      </PolicySection>

      <PolicySection title="How to Initiate a Return">
        <ol className="list-decimal pl-6 space-y-2">
          <li>
            Email{" "}
            <a href="mailto:admin@resurrectedgunparts.com" className="text-[#C0A062] hover:underline">
              admin@resurrectedgunparts.com
            </a>{" "}
            with your order number, the reason for return, and photos if applicable.
          </li>
          <li>We will respond within 2 business days with a Return Merchandise Authorization (RMA) number and return instructions.</li>
          <li>Ship the item back in its original packaging with the RMA number clearly marked on the outside of the box.</li>
          <li>Once received and inspected, your refund will be processed to the original payment method within 5–7 business days.</li>
        </ol>
        <p className="text-[#71717a] text-xs italic">
          Returns received without a prior RMA may be refused or delayed.
        </p>
      </PolicySection>

      <PolicySection title="Damaged in Transit">
        <p>
          If your package arrives damaged, take photographs of the packaging and contents <em>before unpacking
          further</em> and contact us within 48 hours of delivery. We will work with the carrier to resolve the claim
          and replace your item at no cost to you.
        </p>
      </PolicySection>

      <PolicySection title="Cancellations">
        <p>
          Orders may be cancelled for a full refund only if they have not yet shipped. Once tracking has been
          generated, the order is considered in transit and standard return terms apply.
        </p>
      </PolicySection>

      <PolicySection title="Questions">
        <p>
          Email{" "}
          <a href="mailto:admin@resurrectedgunparts.com" className="text-[#C0A062] hover:underline">
            admin@resurrectedgunparts.com
          </a>{" "}
          or call (928) 637-8133. We're a small operation and we read every message personally.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}
